import React, { useEffect, useState } from "react";
import { UserScreen } from "../../../layouts/user-screen.tsx";
import { FontStyles } from "../../../utils/font-styles.ts";
import { Button } from "../../../components/buttons/button.tsx";
import { Color } from "../../../components/buttons/button-variants.ts";
import { Divider } from "../../../components/divider/divider.tsx";
import { setPopupAlertInLocalStorage } from "../../../components/alerts/popup-alert.tsx";
import { Alert } from "../../../components/alerts/alert.tsx";
import { TrackEvent } from "../../../utils/analytics.ts";

const textStyle = `font-normal ${FontStyles[`body-l`]}`;
const bulletPointTextStyle = `font-medium ${FontStyles[`body-l`]}`;

export const MarketingPreferencesScreen: React.FC = () => {
  useEffect(() => {
    TrackEvent({ name: "my_espc_account_activated", value: 50 });
  }, []);

  const bgImage = "/static/images/web-promo-teal-narrow.webp";
  const [disableSave, setDisableSave] = useState(true);
  const [yesButton, setYesButton] = useState<Color>("light");
  const [noButton, setNoButton] = useState<Color>("light");
  const [yes, setYes] = useState(false);
  const [error, setError] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const onChoiceClick = (yes: boolean) => {
    if (yes) {
      setYesButton("secondary");
      setNoButton("light");
      setYes(true);
    } else {
      setNoButton("secondary");
      setYesButton("light");
      setYes(false);
    }
    setDisableSave(false);
  };

  const updatePreferences = async () => {
    if (!disableSave) {
      setSaveLoading(true);
      try {
        const response = await fetch(`/users/update-marketing?allowNewsletter=${yes.toString()}`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });
        if (response.ok) {
          setPopupAlertInLocalStorage({
            color: "green",
            style: "solid",
            message: "Your preferences have been saved. You have been signed in.",
          });
          window.location.href = "/";
        } else {
          setError(true);
          setSaveLoading(false);
        }
      } catch {
        setError(true);
        setSaveLoading(false);
      }
    }
  };

  function BulletPoint(text: string) {
    return (
      <div className="flex gap-2.5 items-center">
        <i className="fa-sharp fa-regular text-2xl fa-circle-check text-loch" />
        <p className={bulletPointTextStyle}>{text}</p>
      </div>
    );
  }

  return (
    <UserScreen image={bgImage} headerText="Welcome to My ESPC" dismissible={false}>
      <div className="flex flex-col relative gap-4.5 md:gap-6 px-4.5 pb-5 pt-2 md:px-7.5 md:pb-7.5 my-7.5">
        <p className={textStyle}>
          Your email address has been verified and you can start using your account. Your My ESPC account will allow you
          to:
        </p>
        <div className="flex flex-col gap-3.5">
          {BulletPoint("Save searches")}
          {BulletPoint("Create a list of your favourite properties")}
          {BulletPoint("Get alerts for new properties matching your saved searches")}
          {BulletPoint("Get notified when a saved property changes status")}
        </div>
        <Divider />
        {error && (
          <Alert
            message={"There was an error with setting your marketing preferences. Please try again later."}
            color={"red"}
            style={"outlined"}
            renderLeadingIcon={() => <i className="fa-sharp fa-regular text-2xl fa-circle-info" />}
          />
        )}
        <div className="flex flex-col gap-2.5">
          <p className={textStyle}>Keep me up to date with property market news and events:</p>
          <div className="grid grid-cols-2 gap-3">
            <Button colorVariant={yesButton} label="Yes" variant="solid" onClick={() => onChoiceClick(true)} />
            <Button colorVariant={noButton} label="No" variant="solid" onClick={() => onChoiceClick(false)} />
          </div>
        </div>
        <Button
          label={"Save preferences"}
          colorVariant="primary"
          loading={saveLoading}
          loadingLabel={"Saving preferences"}
          variant="solid"
          disabled={disableSave || saveLoading}
          onClick={() => {
            void updatePreferences();
          }}
        />
      </div>
    </UserScreen>
  );
};
